import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {PaginationService} from "./pagination.service";
import {
  PhoneEntryFunnelConfig,
  CampaignMenu,
  Campaign,
  CampaignProduct,
  getNextMenuSectionId,
  BaseModel,
  SmartlinkPreviewData,
} from "../_models";
import {BehaviorSubject, Observable} from 'rxjs';
import {ClipboardService} from "./clipboard.service";
import {map} from "rxjs/operators";
import {StorageService} from './storage.service';

@Injectable()
export class CampaignService extends PaginationService {
  private subject = new BehaviorSubject<CampaignMenu>(null);
  private currentCampaignUpdate = new BehaviorSubject<Campaign>(null);

  constructor(
    protected http: HttpClient,
    protected clipboardService: ClipboardService,
    protected storageService: StorageService
  ) {
    super(http, 'campaigns');
  }

  verify(campaign_id: string, token: string) {
    return this.http.post('campaigns/verify/', {'campaign_id': campaign_id, 'token': token});
  }

  summary(campaign_id: string) {
    return this.http.get('campaigns/' + campaign_id.toString() + '/summary/');
  }

  getRandomPreviewProduct(id: string | number, requireVariants: boolean = false) {
    let params = new HttpParams();

    if (requireVariants) {
      params = params.set("require_variants", "true");
    }

    return this.http.get('campaigns/' + id.toString() + '/preview_product/', {params: params});
  }

  savePhoneEntryFunnel(id: string | number, data: PhoneEntryFunnelConfig) {
    return this.http.post('campaigns/' + id.toString() + '/phone_entry/', data);
  }

  showMenu(campaign: Campaign | null, section: string, isNew: boolean, isHybrid: boolean) {
    this.subject.next({ campaign: isNew ? null : campaign, activeSection: section, isHybrid: isHybrid });
  }

  hideMenu() {
    this.subject.next(null);
  }

  getMenu(): Observable<CampaignMenu> {
    return this.subject.asObservable();
  }

  getNextMenuRoute() {
    return this.getMenu().pipe(map((menu: CampaignMenu) => {
      if (!menu) {
        return null
      }
      const menuType = menu.isHybrid ? 'hybrid' : 'normal';

      return getNextMenuSectionId(menuType, menu.activeSection);
    }));
  }

  checkSubdomain(subdomain: string) {
    return this.http.post('subdomain/check/', {subdomain: subdomain});
  }

  copyProducts(products: CampaignProduct[]) {
    this.clipboardService.copy('campaign_products', products);
  }

  canPasteProducts(): boolean {
    return this.clipboardService.canPaste('campaign_products');
  }

  pasteProducts() {
    return this.clipboardService.paste('campaign_products');
  }

  selectCurrentCampaign(campaign: Campaign) {
    this.storageService.set('currentCampaign', campaign)
  }

  getCurrentCampaign(): Campaign {
    return this.storageService.get('currentCampaign') || null;
  }

  getCurrentCampaignId(): string | number {
    const campaign = this.getCurrentCampaign();
    return campaign && campaign.id;
  }

  removeCurrentCampaign() {
    this.storageService.remove('currentCampaign');
  }

  currentCampaignUpdate$() {
    return this.currentCampaignUpdate.asObservable();
  }

  currentCampaignUpdated(campaign: Campaign) {
    this.currentCampaignUpdate.next(campaign);
    this.selectCurrentCampaign(campaign);
  }

  update(id, obj: BaseModel) {
    return this.http.put(this.route + '/' + id.toString() + '/', obj).pipe(
      map((campaign: Campaign) => {
        const mergedCampaign = { ...this.getCurrentCampaign(), ...obj };
        this.currentCampaignUpdated(mergedCampaign);
        return campaign;
      })
    );
  }

  patch(id, data: {}) {
    return this.http.patch(this.route + '/' + id.toString() + '/', data).pipe(
      map((campaign: Campaign) => {
        const mergedCampaign = { ...this.getCurrentCampaign(), ...data };
        this.currentCampaignUpdated(mergedCampaign);
        return campaign;
      })
    );
  }

  refresh() {
    const campaign = this.getCurrentCampaign();

    if (campaign) {
      this.get(campaign.id).subscribe((campaign: Campaign) => this.currentCampaignUpdated(campaign));
    }
  }

  startSession(id: string | number, data: {}) {
    return this.http.post(this.route + '/' + id.toString() + '/start_session/', data);
  }

  clearSmartlinkPreviewData(id: string | number) {
    sessionStorage.removeItem('smartlink_campaign_' + id.toString());
  }

  getSmartlinkPreviewData(id: string | number) {
    const value = sessionStorage.getItem('smartlink_campaign_' + id.toString());
    return value ? JSON.parse(value) : null;
  }

  setSmartlinkPreviewData(id: string | number, data: SmartlinkPreviewData) {
    sessionStorage.setItem('smartlink_campaign_' + id.toString(), JSON.stringify(data));
  }
}
